import { useEffect, useMemo, useState } from 'react'
import { FileText, Plus, Search } from 'lucide-react'
import { Link } from 'react-router'
import { Badge } from 'tailwind-v4/components/badge'
import { Button } from 'tailwind-v4/components/button'
import { Input } from 'tailwind-v4/components/input'
import { Spinner } from 'tailwind-v4/components/spinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import { cn } from 'tailwind-v4/lib/utils'
import inboxApi from '@/state/inbox/inbox.slice'
import { useVenues } from '@/hooks/useVenue'
import { emailBodyClasses } from '../messaging/message-row'

interface PassedProps {
  orgId: string
  threadId: string
  serial?: string
  /** Receives the rendered (merge-tags substituted) template HTML. */
  onInsert: (html: string) => void
  /**
   * Extra classes for the trigger. The composer passes row classes when this
   * sits inside its mobile `⋯` popover instead of the editor toolbar.
   */
  className?: string
}

/**
 * Composer toolbar button: opens a Finder-style template picker — searchable
 * list on the left, a live (merge-tags resolved) preview on the right — and
 * inserts the chosen template at the cursor on "Use template".
 */
export function TemplateInsertButton({
  orgId,
  threadId,
  serial,
  onInsert,
  className,
}: PassedProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data } = inboxApi.useGetMessageTemplatesQuery({ orgId })
  const [renderTemplate, { data: rendered, isFetching, originalArgs }] =
    inboxApi.useLazyRenderMessageTemplateQuery()
  const [, findVenueAlias] = useVenues()

  const allTemplates = useMemo(() => data?.data ?? [], [data])

  // Show every template, but a venue-specific one is only *usable* when it
  // matches the thread's venue — otherwise it's shown disabled with its venue
  // labelled, so staff see it exists (just not for this venue) rather than
  // thinking it vanished. Usable ones sort first.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allTemplates
      .filter((t) => !term || t.name.toLowerCase().includes(term))
      .map((t) => ({
        template: t,
        usable: t.serial === null || t.serial === serial,
        venueName: t.serial ? findVenueAlias(t.serial) : null,
      }))
      .sort((a, b) => Number(b.usable) - Number(a.usable))
  }, [allTemplates, search, serial, findVenueAlias])

  // Render the selected template (resolves merge tags against the thread) for
  // the preview pane.
  useEffect(() => {
    if (open && selectedId) {
      renderTemplate({ orgId, threadId, templateId: selectedId })
    }
  }, [open, selectedId, orgId, threadId, renderTemplate])

  const previewReady =
    !isFetching && rendered && originalArgs?.templateId === selectedId

  const use = () => {
    if (!previewReady || !rendered) return
    onInsert(rendered.body)
    setOpen(false)
    setSelectedId(null)
    setSearch('')
  }

  return (
    <>
      <button
        type="button"
        title="Insert template"
        onClick={() => {
          setOpen(true)
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
          // Neutral toolbar action — see the note on MergeTagInsertButton.
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          className
        )}
      >
        <FileText className="size-3" />
        Template
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[80dvh] w-[min(900px,92vw)] !max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b border-black/10 px-5 py-4 dark:border-white/10">
            <DialogTitle className="text-base">Insert a template</DialogTitle>
            {serial ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                This conversation is for
                <Badge variant="secondary">{findVenueAlias(serial)}</Badge>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Not tied to a specific venue — only organisation-wide templates
                can be used.
              </p>
            )}
          </DialogHeader>

          {allTemplates.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              <p className="text-sm text-muted-foreground">No templates yet.</p>
              <Link
                to={`/${orgId}/inbox/templates`}
                onClick={() => {
                  setOpen(false)
                }}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                <Plus className="size-3.5" />
                Create a template
              </Link>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1">
              {/* Left: searchable template list */}
              <aside className="flex w-64 shrink-0 flex-col border-r border-black/10 dark:border-white/10">
                <div className="border-b border-black/10 p-2 dark:border-white/10">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="h-9 pl-9"
                      placeholder="Search templates…"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value)
                      }}
                    />
                  </div>
                </div>
                <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
                  {filtered.length === 0 ? (
                    <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                      No matches.
                    </p>
                  ) : null}
                  {filtered.map(({ template, usable, venueName }) => (
                    <button
                      key={template.id}
                      type="button"
                      disabled={!usable}
                      title={
                        usable
                          ? undefined
                          : `Only available at ${venueName ?? 'another venue'}`
                      }
                      onClick={() => usable && setSelectedId(template.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                        !usable
                          ? 'cursor-not-allowed text-muted-foreground/60'
                          : selectedId === template.id
                            ? 'bg-black/[0.06] font-medium text-foreground dark:bg-white/10'
                            : 'text-muted-foreground hover:bg-black/[0.04] hover:text-foreground dark:hover:bg-white/5'
                      )}
                    >
                      <FileText className="size-4 shrink-0 opacity-70" />
                      <span className="min-w-0 flex-1 truncate">
                        {template.name}
                      </span>
                      {venueName ? (
                        <span
                          className={cn(
                            'max-w-[90px] shrink-0 truncate rounded-full px-1.5 py-0.5 text-[10px]',
                            usable
                              ? 'bg-black/5 text-muted-foreground dark:bg-white/10'
                              : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          )}
                        >
                          {venueName}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </aside>

              {/* Right: preview + use */}
              <section className="flex min-w-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto p-5">
                  {!selectedId ? (
                    <p className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
                      Select a template to preview it.
                    </p>
                  ) : isFetching || !previewReady ? (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      <Spinner />
                    </div>
                  ) : (
                    <div
                      className={emailBodyClasses}
                      dangerouslySetInnerHTML={{ __html: rendered?.body ?? '' }}
                    />
                  )}
                </div>
                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-black/10 px-5 py-3 dark:border-white/10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOpen(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    disabled={!previewReady}
                    onClick={use}
                  >
                    Use template
                  </Button>
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TemplateInsertButton
